import axios from "axios";
import catalogProducts from "@/data/catalogProducts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "");
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const client = axios.create({ baseURL: API });

const FALLBACK_CATEGORIES = [
    { slug: "all", label: "All" },
    { slug: "tops", label: "Tops" },
    { slug: "bottoms", label: "Bottoms" },
];

export function getApiErrorMessage(error, fallback = "Something went wrong.") {
    if (error?.response?.status === 504) {
        return "Checkout provider timed out. Please try again.";
    }

    return (
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        fallback
    );
}

function applyLocalProductFilters(products, params = {}) {
    const { category, size, search, sort } = params;

    let results = [...products];

    if (category && category !== "all") {
        results = results.filter((product) => product.category === category);
    }

    if (size) {
        results = results.filter((product) => product.sizes?.includes(size));
    }

    if (search) {
        const query = search.toLowerCase();
        results = results.filter((product) => product.name.toLowerCase().includes(query));
    }

    if (sort === "price_asc") {
        results.sort((a, b) => a.price - b.price);
    } else if (sort === "price_desc") {
        results.sort((a, b) => b.price - a.price);
    }

    return results;
}

export function getProductSlug(product) {
    const source = product?.name || product?.id || "";
    return source
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^A-Za-z0-9-]/g, "")
        .replace(/-+/g, "-");
}

export function getProductPath(product) {
    return `/product/${getProductSlug(product) || product.id}`;
}

export const fetchProducts = async (params = {}) => {
    if (catalogProducts.length > 0) {
        return applyLocalProductFilters(catalogProducts, params);
    }

    const { data } = await client.get("/products", { params });
    return data;
};

export const fetchProduct = async (id) => {
    const decodedId = decodeURIComponent(id || "");

    if (catalogProducts.length > 0) {
        return (
            catalogProducts.find(
                (product) => product.id === decodedId || getProductSlug(product) === decodedId
            ) ?? null
        );
    }

    const { data } = await client.get(`/products/${encodeURIComponent(decodedId)}`);
    return data;
};

export const fetchCategories = async () => {
    if (catalogProducts.length > 0) {
        return FALLBACK_CATEGORIES;
    }

    const { data } = await client.get("/categories");
    return data.categories;
};

export const subscribeNewsletter = async (email) => {
    const { data } = await client.post("/newsletter", { email });
    return data;
};

export const createCheckoutSession = async (payload) => {
    const { data } = await client.post("/checkout/session", payload);
    return data;
};

export const getCheckoutStatus = async (sessionId) => {
    const { data } = await client.get(`/checkout/status/${sessionId}`);
    return data;
};

export const createManualOrder = async (payload) => {
    const { data } = await client.post("/orders", payload);
    return data;
};

export const fetchAdminOrders = async (password) => {
    const { data } = await client.get("/admin/orders", {
        headers: {
            "X-Admin-Password": password,
        },
    });
    return data;
};

export const createVirtualTryOn = async ({ personImage, garmentImage, productName, setting }) => {
    const formData = new FormData();
    formData.append("person_image", personImage);
    formData.append("garment_image", garmentImage);
    formData.append("product_name", productName);
    formData.append("setting", setting);

    const { data } = await client.post("/virtual-try-on", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });

    return data;
};

export default client;
